/**
 * The single HTTP client. Every request (JSON and SSE) goes through here so the
 * bearer token and the 401/403 handling live in one place.
 */

type TokenProvider = () => Promise<string | null>

let tokenProvider: TokenProvider | null = null
let unauthorizedHandler: (() => void) | null = null

/** Installed by the auth layer once MSAL is ready (entra mode only). */
export function configureAuth(options: { getToken: TokenProvider; onUnauthorized: () => void }): void {
  tokenProvider = options.getToken
  unauthorizedHandler = options.onUnauthorized
}

export class ApiError extends Error {
  readonly status: number
  readonly detail: string

  constructor(status: number, detail: string) {
    super(detail || `Request failed (${status})`)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }

  get isUnauthorized() {
    return this.status === 401
  }

  get isForbidden() {
    return this.status === 403
  }

  get isNotFound() {
    return this.status === 404
  }
}

/** Human-readable message for any thrown value, with the contract's 403 wording. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isForbidden) return "You don't have access."
    if (error.isUnauthorized) return 'Your session has expired. Signing you in again…'
    if (error.status === 0) return error.detail
    return error.detail || `Something went wrong (HTTP ${error.status}).`
  }
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}

/** Like errorMessage, but keeps the server's own wording (form validation, sign-in, uploads). */
export function detailMessage(error: unknown): string {
  if (error instanceof ApiError && error.detail && error.status !== 0) return error.detail
  return errorMessage(error)
}

/**
 * Headers for every API call. `X-Requested-With` is the CSRF guard for cookie sessions
 * (AUTH_MODE=local): browsers only let same-origin scripts set it.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'X-Requested-With': 'fetch' }
  if (!tokenProvider) return headers
  const token = await tokenProvider()
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

/** Read `{ detail }` from an error response; tolerate non-JSON bodies. */
export async function readErrorDetail(response: Response): Promise<string> {
  try {
    const text = await response.text()
    if (!text) return response.statusText
    try {
      const body = JSON.parse(text) as { detail?: unknown }
      if (typeof body.detail === 'string') return body.detail
      if (body.detail !== undefined) return JSON.stringify(body.detail)
    } catch {
      /* not JSON */
    }
    return text.slice(0, 300)
  } catch {
    return response.statusText
  }
}

/** Shared 401 handling for JSON requests and the SSE stream. */
export function handleUnauthorized(): void {
  unauthorizedHandler?.()
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Set false for endpoints that must work before sign-in (/api/meta). */
  auth?: boolean
  query?: Record<string, string | number | boolean | null | undefined>
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, auth = true, query } = options
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Requested-With': 'fetch' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (auth) Object.assign(headers, await authHeaders())

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, 'Could not reach the server. Check your connection and try again.')
  }

  if (!response.ok) {
    const detail = await readErrorDetail(response)
    if (response.status === 401 && auth) handleUnauthorized()
    throw new ApiError(response.status, detail)
  }

  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}
